import { formatCLP, formatUSD } from '../budget/format'
import { currentLocale } from '../../i18n'

/**
 * The arithmetic and formatting both wide capacity sheets share -- Balance
 * mensual (`MonthlyReport`) and the staffing plan (`StaffingMatrix`). Split
 * from `VisibilityMenu.jsx` for the same reason `costLock.js` is split from
 * its provider: a module that exports components and helpers together breaks
 * fast refresh.
 *
 * They are the same table asked of two different plans: what weekly planning
 * recorded, and what the projects committed. Keeping the column header, the
 * number format and the visibility menu here means the two cannot quietly
 * drift into looking like different products.
 */
export const num = (v, digits = 1) =>
  v == null ? '—' : new Intl.NumberFormat(currentLocale(), {
    maximumFractionDigits: digits }).format(v)

export const money = (amount, currency) => (
  amount == null ? '—' : currency === 'USD' ? formatUSD(amount) : formatCLP(amount)
)

export const quarterOf = (month) => Math.floor((month - 1) / 3) + 1

// A project column has to stay narrow -- thirty of them only fit if they do --
// so the header carries the code and the alias, never the full name, which
// lives in the tooltip instead.
// A project that exists only inside a scenario (scenario_id set) carries a ◆, so
// it is never mistaken for the real project it may share a name and code with.
const esc = (p, text) => (p.scenario_id ? `◆ ${text}` : text)
export const projectCode = (p) => esc(p, p.code || p.nickname || p.name)
// Alias first, for the places that name a project in prose rather than in a
// column header: a person's expanded rows read better as "4X4" than "2026-006".
export const projectShortName = (p) => esc(p, p.nickname || p.name || p.code)
export const projectAlias = (p) => (p.nickname && p.nickname !== p.code ? p.nickname : null)
export const projectTitle = (p) => (p.code ? `${p.code} · ${p.name}` : p.name) + (p.scenario_id ? ' · ◆ escenario / scenario' : '')

/** Move `fromId` to where `toId` sits, leaving everything else in order. */
export function reordered(ids, fromId, toId) {
  const next = ids.slice()
  const from = next.indexOf(fromId)
  const to = next.indexOf(toId)
  if (from === -1 || to === -1 || from === to) return ids
  next.splice(to, 0, next.splice(from, 1)[0])
  return next
}

/** Sort by an explicit order of ids; anything not in it keeps its place at the end. */
export function byExplicitOrder(items, order, getId) {
  if (!order.length) return items
  const rank = new Map(order.map((id, i) => [id, i]))
  return items.slice().sort((a, b) =>
    (rank.get(getId(a)) ?? Infinity) - (rank.get(getId(b)) ?? Infinity))
}

/**
 * Keep only the rows and columns that are non-empty within the current
 * selection, in both directions: pick three projects and only the people with
 * hours on them remain; pick three people and only the projects they are on
 * do. One pass in either direction is enough and both give the same answer --
 * a person kept here has hours on some picked project, which therefore has
 * hours from a kept person and survives too, so nothing left standing can
 * empty out afterwards. It is also why "nothing hidden" already means
 * "everything with hours in the period", with no default to configure.
 */
export function crossFilter(pickedRows, pickedProjects, hoursOf) {
  const rows = pickedRows.filter(r => pickedProjects.some(p => hoursOf(r, p) > 0))
  const projects = pickedProjects.filter(p => rows.some(r => hoursOf(r, p) > 0))
  return { rows, projects }
}
